import type { AxiosResponse } from 'axios';
import React, { createContext, useCallback, useContext } from 'react';
import {
	extractOfDataQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	useDataBoxes,
	useProcessControl,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import {
	getRequiredDataBoxes,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import { compose } from '@gov-nx/core/types';
import type { ServiceCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type { FormData, ServiceContextTypes } from './service.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext = createContext<ServiceContextTypes | null>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

const AUTHORIZATION_SERVICE_NAME = 'VEREJNY_REJSTRIK';

export function VypisZVerejnehoRejstrikuContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const dataBoxTypes = getRequiredDataBoxes(code, application);
	const { isDataBoxConnected } = useDataBoxes(dataBoxTypes);
	const { toastMessageSuccess } = useMessageEvents({});
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);

	const { controls, setControls } = useProcessControl({
		initialLoading: false,
	});
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);
	const formDefinition = FormInstance({ dataBoxTypes, isLowAuthorization });

	const submitMutation = usePoMutation<AxiosResponse<void>, FormData>({
		mutationFn: compose(extractOfDataQuery(axiosInstance), prepareSubmitData),
		onSuccess: (response) => {
			setControls({ processLoading: false });
			formDefinition.formReset();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(tsn('form.messages.odeslano'));
			};

			if (authContext) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					response.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onError: (error) => {
			setControls({ processError: error, processLoading: false });
			formDefinition.formReset();
		},
	});

	const handleSubmit = useCallback(async () => {
		setControls({ processError: null, processLoading: true });

		return submitMutation.mutate(formDefinition.formMethods.getValues());
	}, [formDefinition.formMethods, setControls, submitMutation]);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);

	return (
		<ServiceContext.Provider
			value={{
				code,
				isDataBoxConnected,
				dataBoxTypes,
				formDefinition,
				onSubmit,
				submitMutation,
				controls,
				setControls,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useVypisZVerejnehoRejstrikuContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
